import { useGetAssessmentProgresses } from "@/api/console/assessment-progresses/useGetAssessmentProgresses";
import AdminNavbar from "@/components/AdminNavbar/AdminNavbar";
import { InputWithIcon } from "@/components/ui/input";
import { STATUS } from "@/config/progress";
import { handleCompanyName } from "@/lib/handleName";
import CertificationTable from "@/src/console/(auth)/certifications/_components/CertificationTable";
import { BuildingRegular } from "@fluentui/react-icons";
import { addYears, isBefore } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
// Interface for Certification
interface Certification {
  id: number;
  company_name: string;
  issued_at: Date | null;
  status: number;
  submitted_at: Date | null;
}

// Status mapping
const STATUS_MAP = {
  ALL: 0,
  APPLIED: 1,
  CERTIFIED: 2,
  EXPIRED: 3,
  NOT_ISSUED: 4,
};

// Convert status based on rules
const convertStatus = (
  statusId: number | null,
  issued_at: Date | null,
): number => {
  if (statusId === null) return STATUS_MAP.NOT_ISSUED;
  if (
    statusId === STATUS.ASSESSMENT_COMPLETED ||
    statusId === STATUS.RE_ASSESSMENT_COMPLETED
  ) {
    return STATUS_MAP.NOT_ISSUED;
  } else if (statusId === STATUS.APPLIED_FOR_CERTIFICATE) {
    return STATUS_MAP.APPLIED;
  } else {
    return issued_at && isBefore(new Date(), addYears(issued_at, 1))
      ? STATUS_MAP.CERTIFIED
      : STATUS_MAP.EXPIRED;
  }
};

function Certifications() {
  const { t } = useTranslation();
  const statues = [
    STATUS.ASSESSMENT_COMPLETED,
    STATUS.RE_ASSESSMENT_COMPLETED,
    STATUS.APPLIED_FOR_CERTIFICATE,
    STATUS.CERTIFICATED_ISSUED,
  ];
  const { data, isLoading } = useGetAssessmentProgresses(statues.join(","));

  // Map certifications based on the API response
  const certifications: Certification[] =
    data && data.data
      ? data.data.map(
          ({ status, submitted_at, company_name, issued_at, id }) => ({
            id,
            company_name: handleCompanyName(id, company_name),
            issued_at: issued_at ? new Date(issued_at) : null,
            status: convertStatus(
              status.id,
              issued_at ? new Date(issued_at) : null,
            ),
            submitted_at: submitted_at ? new Date(submitted_at) : null,
          }),
        )
      : [];

  const breadcrumbs = [
    {
      name: t("certification"),
      href: "/console/certifications",
    },
  ];

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(STATUS_MAP.ALL);

  // Search and filter certifications
  const searchedCertifications = certifications.filter((certification) =>
    certification.company_name.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredCertifications = searchedCertifications.filter((submission) => {
    if (filters === STATUS_MAP.ALL) return true;
    return submission.status === filters;
  });

  return (
    <div>
      <div className="bg-white shadow">
        <AdminNavbar breadcrumbs={breadcrumbs} />
        <div className="my-heading px-6 pt-8">{t("check-submissions")}</div>
        <div className="flex justify-between px-6 pb-6 pt-8">
          <InputWithIcon
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            Icon={BuildingRegular}
            placeholder="Search"
          />
        </div>
      </div>
      <div className="m-6">
        <CertificationTable
          data={filteredCertifications}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

export default Certifications;
